import { useFieldArray } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { useMemo } from 'react';
import { useQuery } from 'react-query';

import { Tooltip } from 'primereact/tooltip';
import PropTypes from 'prop-types';
import { getAll as getAllOrganizations } from 'apis/organizations.api';
import { getAcademicDegrees, getAcademicRanks } from 'utils/func';

import { TextareaInput, Select } from 'components/FormControl';

export default function ParticipantTable({ disabled, control, errors }) {
  // #region Data
  const { t } = useTranslation();
  const { fields, insert, remove } = useFieldArray({
    control,
    name: 'registerResultAuthors',
  });

  const { data: organizationsData } = useQuery(['organizations'], getAllOrganizations);
  const organizations = useMemo(
    () =>
      organizationsData?.data.map((e) => ({
        ...e,
        name: t('lang') === 'vi' ? e?.name : e?.englishName,
      })) ?? [],
    [organizationsData, t]
  );
  // #endregion Data
  return (
    <div className="form-table">
      <table className="mb-2">
        <thead>
          <tr>
            <th className="text-center" style={{ minWidth: 50, width: 50 }}>
              {t('formLayout.numericalOrder')}
            </th>
            <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
              {t('r09.form.email')}
            </th>
            <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
              {t('r09.form.fullName')}
            </th>
            <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
              {t('r09.form.academicRank')}
            </th>
            <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
              {t('r09.form.academicDegree')}
            </th>
            <th className="text-center" style={{ minWidth: 220, width: '20%' }}>
              {t('r09.form.organization')}
            </th>
            {!disabled && (
              <th className="text-center float" style={{ minWidth: 60, width: 60 }}>
                {t('projectProposal.action')}
              </th>
            )}
          </tr>
        </thead>
        <tbody>
          {fields?.map((item, index) => (
            <tr key={item.id}>
              <td className="text-center">{index + 1}</td>
              <td>
                <TextareaInput
                  name={`registerResultAuthors.${index}.email`}
                  control={control}
                  errors={errors}
                  disabled={disabled}
                />
              </td>
              <td>
                <TextareaInput
                  name={`registerResultAuthors.${index}.fullName`}
                  control={control}
                  errors={errors}
                  disabled={disabled}
                />
              </td>
              <td>
                <Select
                  name={`registerResultAuthors.${index}.academicRank`}
                  control={control}
                  errors={errors}
                  options={getAcademicRanks(t)}
                  inputField="value"
                  defaultValue=""
                  disabled={disabled}
                />
              </td>
              <td>
                <Select
                  name={`registerResultAuthors.${index}.academicDegree`}
                  control={control}
                  errors={errors}
                  options={getAcademicDegrees(t)}
                  inputField="value"
                  disabled={disabled}
                />
              </td>
              <td>
                <Select
                  name={`registerResultAuthors.${index}.organizationId`}
                  options={organizations}
                  inputField="id"
                  renderField="name"
                  control={control}
                  errors={errors}
                  disabled={disabled}
                />
              </td>

              {!disabled && (
                <td className="text-center p-0 float">
                  <button
                    className="p-button p-button-text p-button-icon-only p-0"
                    type="button"
                    onClick={() => remove(index)}
                  >
                    <i
                      className="remove-icon pi pi-times text-red-700 cursor-pointer mb-1"
                      data-pr-tooltip={t('formLayout.action.remove')}
                      data-pr-position="left"
                    />
                  </button>
                  <Tooltip target=".remove-icon" />

                  <div className="">
                    <button
                      className="p-button p-button-text p-button-icon-only p-0"
                      type="button"
                      onClick={() => insert(index + 1)}
                    >
                      <i
                        className="insert-icon pi pi-plus text-primary cursor-pointer mt-1"
                        data-pr-tooltip={t('formLayout.action.insert')}
                        data-pr-position="left"
                      />
                    </button>
                    <Tooltip target=".insert-icon" />
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>
      {!disabled && fields?.length === 0 && (
        <div className="flex justify-content-center my-2">
          <i
            onClick={() => insert(0)}
            className="add-recommended-expert pi pi-plus text-primary cursor-pointer bg-primary p-1 border-circle"
            data-pr-tooltip={t('formLayout.action.add')}
            data-pr-position="left"
          />
          <Tooltip target=".add-recommended-expert" />
        </div>
      )}
    </div>
  );
}

ParticipantTable.propTypes = {
  disabled: PropTypes.bool,
  errors: PropTypes.shape({}).isRequired,
  control: PropTypes.shape({}).isRequired,
};

ParticipantTable.defaultProps = {
  disabled: false,
};
